import { match } from "ts-pattern";
import { createWithEqualityFn } from "zustand/traditional";
import type { CompartmentType } from "../components/renderer/data/Compartment";

export enum AppModus {
	form = "1",
	layout = "2",
	compartment = "3",
}

export enum LayoutModus {
	move = "1",
	merge = "2",
	addHorizontal = "3",
	addVertical = "4",
	distributeVertical = "5",
	distributeHorizontal = "6",
	background = "7",
}

export enum CompartmentModus {
	DoorLeft = "1",
	DoorRight = "2",
	DoorDouble = "3",
	AddShelf = "4",
	RemoveShelf = "5",
	RemoveDoor = "6",
}

interface CompartmentStoreProps {
	appMode: AppModus | null;
	setAppMode: (mode: AppModus | null) => void;

	layoutMode: LayoutModus | null;
	setLayoutMode: (mode: LayoutModus | null) => void;

	compartmentMode: CompartmentModus | null;
	setCompartmentMode: (mode: CompartmentModus | null) => void;

	showDimensions: boolean;
	setShowDimensions: (value: boolean) => void;

	dimensionUnit: "cm" | "mm";
	toggleDimensionUnit: () => void;

	altControls: boolean;
	setAltControls: (value: boolean) => void;

	allCompartmentsOpen: boolean;
	setAllCompartmentsOpen: (value: boolean) => void;

	hoveredCompartmentId: string | null;
	setHoveredCompartmentId: (value: string | null) => void;

	selectedCompartmentIds: string[];
	selectCompartment: (value: CompartmentType) => void;
	deselectCompartment: (value: CompartmentType) => void;
	deselectAllCompartments: () => void;

	fullScreen: boolean;
	setFullScreen: (value: boolean) => void;
}

export const useCanvasStore = createWithEqualityFn<CompartmentStoreProps>((set) => ({
	appMode: AppModus.form,
	setAppMode: (appMode) => set({ appMode }),

	layoutMode: LayoutModus.move,
	setLayoutMode: (layoutMode) => set({ layoutMode }),

	compartmentMode: null,
	setCompartmentMode: (compartmentMode) => set({ compartmentMode }),

	showDimensions: false,
	setShowDimensions: (showDimensions) => set({ showDimensions }),

	dimensionUnit: "mm",
	toggleDimensionUnit: () =>
		set((state) => ({
			dimensionUnit: state.dimensionUnit === "cm" ? "mm" : "cm",
		})),

	altControls: false,
	setAltControls: (altControls) => set({ altControls }),

	allCompartmentsOpen: false,
	setAllCompartmentsOpen: (allCompartmentsOpen) => set({ allCompartmentsOpen }),

	hoveredCompartmentId: null,
	setHoveredCompartmentId: (hoveredCompartmentId) => set({ hoveredCompartmentId }),

	selectedCompartmentIds: [],
	selectCompartment: (compartment) =>
		set((state) => {
			const compartments = match(state.layoutMode)
				.with(LayoutModus.distributeHorizontal, LayoutModus.distributeVertical, () => [
					...state.selectedCompartmentIds,
					compartment.id,
				])
				.otherwise(() => [compartment.id]);

			return { selectedCompartmentIds: compartments };
		}),
	deselectCompartment: (compartment) =>
		set((state) => ({
			selectedCompartmentIds: state.selectedCompartmentIds.filter((c) => c !== compartment.id),
		})),
	deselectAllCompartments: () => set({ selectedCompartmentIds: [] }),

	fullScreen: false,
	setFullScreen: (fullScreen) => set({ fullScreen }),
}));
